import React from "react";
import styled from "@emotion/styled/macro";
import { Typography } from "@sunrun/experience-ui-components";
import { isCapPackageCustomerSigned } from "../../constants/capPackageStatus";
import { shouldHideCompletedPpcWithoutAppointment } from "../../constants/postCapHomeCheck";
import { useCapContext } from "../../providers/CapContext";
import { usePostCapHomeCheck } from "../../hooks/usePostCapHomeCheck";
import { PostCapHomeCheckScheduler } from "./PostCapHomeCheckScheduler";

type PostCapHomeCheckSectionProps = {
  docTextTitleSize: number;
  docTextDescriptionSize: number;
  isTablet: boolean;
};

const PostCapHomeCheckSection: React.FC<PostCapHomeCheckSectionProps> = ({
  docTextTitleSize,
  docTextDescriptionSize,
  isTablet,
}) => {
  const { prospectId, prospect, documents, sow, mutateSow } = useCapContext();
  const canScheduleHomeCheck = isCapPackageCustomerSigned(documents);
  const scheduling = usePostCapHomeCheck(prospectId, { mutateSow });

  if (!prospectId) {
    return null;
  }

  if (
    shouldHideCompletedPpcWithoutAppointment(
      sow?.otherCapReqs,
      sow?.otherCapReqsFulfilled,
      scheduling.bookingSnapshotLoading,
      scheduling.scheduledAppointment
    )
  ) {
    return null;
  }

  return (
    <Row showColumnStyle={isTablet} isFirstRowTabletView={false}>
      <BaseSidebarText isTablet={isTablet}>
        <Typography variant="h4" size={docTextTitleSize}>
          Pre-permit check
        </Typography>
        <Typography size={docTextDescriptionSize}>
          A Site Technician will visit your home to collect a few details for
          the installation crew.
          <br />
          Once you’ve scheduled the pre-permit check, the status might take a
          few minutes to update. Please refresh this page.
        </Typography>
      </BaseSidebarText>
      <BaseContainer isTablet={isTablet}>
        <PostCapHomeCheckScheduler
          prospectTimeZoneId={prospect?.timeZoneId}
          canSchedule={canScheduleHomeCheck}
          bookingSnapshotLoading={scheduling.bookingSnapshotLoading}
          scheduledAppointment={scheduling.scheduledAppointment}
          slotBookingParentId={scheduling.slotBookingParentId}
          slots={scheduling.slots}
          slotsLoading={scheduling.slotsLoading}
          slotsError={scheduling.slotsError}
          retrySlots={scheduling.retrySlots}
          bookingLoading={scheduling.bookingLoading}
          rescheduleLoading={scheduling.rescheduleLoading}
          bookSlot={scheduling.bookSlot}
          rescheduleAppointment={scheduling.rescheduleAppointment}
        />
      </BaseContainer>
    </Row>
  );
};

const BaseSidebarText = styled.div<{ isTablet: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isTablet }) => (isTablet ? "100%" : "30%")};
  gap: ${({ isTablet }) => (isTablet ? "4px" : "12px")};
`;

const BaseContainer = styled.div<{ isTablet: boolean }>`
  width: ${({ isTablet }) => (isTablet ? "100%" : "70%")};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div<{
  showColumnStyle: boolean;
  isFirstRowTabletView: boolean;
}>`
  padding-top: ${({ isFirstRowTabletView }) =>
    isFirstRowTabletView ? "12px" : "40px"};
  display: flex;
  flex-direction: ${({ showColumnStyle }) =>
    showColumnStyle ? "column" : "row"};
  gap: 12px;
`;

export { PostCapHomeCheckSection };
export type { PostCapHomeCheckSectionProps };
